import Delete from "@mui/icons-material/Delete";
import {
  Box,
  Grid2,
  IconButton,
  List,
  ListItem,
  ListItemText,
} from "@mui/material";
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useState,
} from "react";
import { DropzoneProps, DropzoneRootProps, useDropzone } from "react-dropzone";
import styled from "styled-components";
import { memoObserver } from "../../utils/memoObserver.utils";

export interface VDUploadProps extends DropzoneProps {
  defaultValue?: any;
}

export type FileUpload = File | string;

export interface VDUploadRef extends HTMLInputElement {
  getFiles: () => File[];
  getInitialFiles: () => string[];
  getDeleteFiles: () => string[];
}

export const CUpload = memoObserver(
  forwardRef<VDUploadRef, VDUploadProps>((props, ref) => {
    const [files, setFiles] = useState<File[]>([]);
    const [deleteFiles, setDeleteFiles] = useState<string[]>([]);
    const [initialFiles, setInitialFiles] = useState<string[]>([]);
    const onDrop = useCallback(
      (acceptedFiles: File[]) => {
        setFiles([...files, ...acceptedFiles]);
      },
      [files]
    );
    const {
      getRootProps,
      getInputProps,
      isDragActive,
      isFocused,
      isDragReject,
      isDragAccept,
      inputRef,
    } = useDropzone({
      onDrop,
    });

    useEffect(() => {
      if (props.defaultValue) {
        setInitialFiles(props.defaultValue);
      }
    }, [props.defaultValue]);

    useImperativeHandle(ref, () => ({
      ...(inputRef?.current as HTMLInputElement),
      getFiles: () => files,
      getInitialFiles: () => initialFiles,
      getDeleteFiles: () => deleteFiles,
    }));

    const removeFile = (index: number, isInitialFile: boolean) => () => {
      if (isInitialFile) {
        const newInitialFiles = [...initialFiles];
        newInitialFiles.splice(index, 1);
        setDeleteFiles([...deleteFiles, initialFiles[index]]);
        setInitialFiles(newInitialFiles);
      } else {
        const newFiles = [...files];
        newFiles.splice(index, 1);
        setFiles(newFiles);
      }
    };

    const getFilePreview = (file: FileUpload) => {
      if (typeof file === "string") {
        // If it's a URL, return the URL itself
        return file;
      } else {
        // For File objects, create a temporary object URL
        return URL.createObjectURL(file);
      }
    };

    const filesList = [
      ...initialFiles.map((file, index) => (
        <Box
          key={file}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <img
              style={{ objectFit: "cover" }}
              src={getFilePreview(file)}
              alt={`uploaded ${index}`}
              width="100"
              height="100"
            />
          </Box>
          <IconButton onClick={removeFile(index, true)}>
            <Delete />
          </IconButton>
        </Box>
      )),
      ...files.map((file, index) => (
        <Box
          key={file.name}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box>
            <img
              style={{ objectFit: "cover" }}
              src={getFilePreview(file)}
              alt={file.name}
              width="100"
              height="100"
            />
          </Box>
          <IconButton onClick={removeFile(index, false)}>
            <Delete />
          </IconButton>
        </Box>
      )),
    ];

    return (
      <Grid2>
        <Container {...getRootProps({ isFocused, isDragAccept, isDragReject })}>
          <input {...getInputProps()} />
          {isDragActive ? (
            <p>Solte arquivos aqui...</p>
          ) : (
            <p>
              Arraste e solte alguns arquivos aqui ou clique para selecionar os
              arquivos
            </p>
          )}
        </Container>
        <List>{filesList}</List>
      </Grid2>
    );
  })
);

const Container = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 20px;
  border-width: 2px;
  border-radius: 2px;
  border-color: ${(props) => getColor(props)};
  border-style: dashed;
  background-color: #fafafa;
  color: #bdbdbd;
  outline: none;
  transition: border 0.24s ease-in-out;
  cursor: pointer;
`;

const getColor = (props: DropzoneRootProps) => {
  if (props.isDragAccept) {
    return "#00e676";
  }
  if (props.isDragReject) {
    return "#ff1744";
  }
  if (props.isFocused) {
    return "#2196f3";
  }
  return "#eeeeee";
};

function truncatedText(text: string) {
  const MAX_LENGTH = 30;
  return text?.length > MAX_LENGTH
    ? text?.substring(0, MAX_LENGTH) + "..."
    : text;
}
